import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Send, Trash2, Upload, UserPlus, AlertTriangle, Search, MapPin, Star } from "lucide-react";

const STATUS = { nuevo: "NUEVO", redactado: "REDACTADO", contactado: "CONTACTADO" };
const STATUS_COLOR = { nuevo: "var(--text-dim)", redactado: "var(--warn)", contactado: "var(--accent)" };

export default function Captacion() {
  const [prospects, setProspects] = useState([]);
  const [actions, setActions] = useState([]);
  const [form, setForm] = useState({ business_name: "", contact: "", sector: "", notes: "" });
  const [bulk, setBulk] = useState("");
  const [busy, setBusy] = useState(null);
  const [sendAction, setSendAction] = useState("");
  const [search, setSearch] = useState({ sector: "", ciudad: "" });
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState({});
  const [searching, setSearching] = useState(false);

  const load = () => api.get("/prospects").then((r) => setProspects(r.data.prospects || []));
  useEffect(() => {
    load();
    api.get("/actions").then((r) => { setActions(r.data.actions || []); if (r.data.actions?.[0]) setSendAction(r.data.actions[0].id); }).catch(() => {});
  }, []);

  const add = async () => {
    if (!form.business_name.trim()) return toast.error("Nombre del negocio requerido");
    await api.post("/prospects", form);
    setForm({ business_name: "", contact: "", sector: "", notes: "" }); load();
  };
  const importBulk = async () => {
    if (!bulk.trim()) return;
    const { data } = await api.post("/prospects/import", { text: bulk });
    toast.success(`${data.added} prospectos añadidos`); setBulk(""); load();
  };
  const draft = async (p) => {
    setBusy(p.id + "-draft");
    try { const { data } = await api.post(`/prospects/${p.id}/draft`);
      setProspects((ps) => ps.map((x) => x.id === p.id ? { ...x, message: data.message, status: "redactado" } : x));
    } catch (e) { toast.error(e.response?.data?.detail || "Error al redactar"); }
    finally { setBusy(null); }
  };
  const saveMsg = (p, message) => setProspects((ps) => ps.map((x) => x.id === p.id ? { ...x, message } : x));
  const send = async (p) => {
    if (!sendAction) return toast.error("Conecta y elige una acción de envío (Acciones n8n)");
    if (!p.message?.trim()) return toast.error("Redacta el mensaje primero");
    setBusy(p.id + "-send");
    try {
      await api.patch(`/prospects/${p.id}`, { message: p.message });
      await api.post(`/prospects/${p.id}/send`, { action_id: sendAction });
      toast.success("Enviado"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar"); }
    finally { setBusy(null); }
  };
  const del = async (p) => { await api.delete(`/prospects/${p.id}`); load(); };

  const doSearch = async () => {
    if (!search.sector.trim() && !search.ciudad.trim()) return toast.error("Pon al menos sector y ciudad");
    setSearching(true); setResults([]); setPicked({});
    try {
      const { data } = await api.post("/prospects/search", search);
      setResults(data.results || []);
      if (!(data.results || []).length) toast("Sin resultados. Prueba otra búsqueda.");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al buscar"); }
    finally { setSearching(false); }
  };
  const addPicked = async () => {
    const sel = results.filter((_, i) => picked[i]);
    if (!sel.length) return toast.error("Marca algún negocio primero");
    setBusy("import-search");
    try {
      for (const b of sel) {
        await api.post("/prospects", {
          business_name: b.nombre,
          contact: b.telefono || b.web || "",
          sector: search.sector || "",
          notes: [b.direccion, b.web, b.rating ? "⭐ " + b.rating : ""].filter(Boolean).join(" · ").slice(0, 300),
        });
      }
      toast.success(`${sel.length} añadidos a prospectos`);
      setResults([]); setPicked({}); load();
    } catch (e) { toast.error("Error al importar"); }
    finally { setBusy(null); }
  };

  return (
    <div className="p-8 space-y-6" data-testid="captacion-page">
      <div className="border-b border-[var(--border)] pb-5">
        <div className="label-mono mb-2">CAPTACIÓN DE CLIENTES</div>
        <h1 className="font-display font-black text-4xl tracking-tight">Prospectos</h1>
        <p className="text-sm text-[var(--text-dim)] mt-1">
          Añade negocios a los que quieres vender, la IA redacta un mensaje personalizado, y lo envías por tu WhatsApp/email de n8n.
        </p>
      </div>

      {/* Aviso legal */}
      <div className="border border-[var(--warn)] bg-[var(--warn)]/5 p-4 flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 mt-0.5" style={{ color: "var(--warn)" }} />
        <p className="text-xs text-[var(--text-dim)] leading-relaxed">
          <strong>Contacto en frío, con cabeza:</strong> el email comercial B2B y sobre todo el WhatsApp tienen reglas (RGPD/LSSI, y WhatsApp
          prohíbe mensajes masivos no solicitados — pueden banearte el número). Recomendación: personaliza, no hagas spam masivo, incluye
          quién eres y cómo darse de baja, y prioriza a quien tenga relación contigo. Tú decides qué enviar; revisa siempre antes.
        </p>
      </div>

      {/* Buscar negocios (Google Places) */}
      <div className="border border-[var(--accent)]/50 bg-[var(--surface)] p-5 space-y-3">
        <div className="label-mono flex items-center gap-2 text-[var(--accent)]"><Search className="w-3.5 h-3.5" /> BUSCAR NEGOCIOS (GOOGLE)</div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
          <Input placeholder="Sector (p.ej. peluquerías)" value={search.sector}
                 onChange={(e) => setSearch({ ...search, sector: e.target.value })}
                 onKeyDown={(e) => e.key === "Enter" && doSearch()}
                 className="rounded-none bg-[var(--bg)] border-[var(--border)]" data-testid="search-sector" />
          <Input placeholder="Ciudad (p.ej. Palma)" value={search.ciudad}
                 onChange={(e) => setSearch({ ...search, ciudad: e.target.value })}
                 onKeyDown={(e) => e.key === "Enter" && doSearch()}
                 className="rounded-none bg-[var(--bg)] border-[var(--border)]" data-testid="search-city" />
          <Button onClick={doSearch} disabled={searching}
                  className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="search-go">
            <Search className="w-3.5 h-3.5 mr-1" /> {searching ? "Buscando…" : "Buscar"}
          </Button>
        </div>

        {results.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="label-mono">{results.length} resultados · marca los que quieras</span>
              <Button onClick={addPicked} disabled={busy === "import-search"}
                      className="rounded-none h-8 bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="search-import">
                {busy === "import-search" ? "Añadiendo…" : "Añadir seleccionados"}
              </Button>
            </div>
            <div className="max-h-80 overflow-auto border border-[var(--border)]">
              {results.map((b, i) => (
                <label key={i} className="flex items-start gap-3 p-2.5 border-b border-[var(--border)] hover:bg-[var(--bg)]/50 cursor-pointer">
                  <input type="checkbox" checked={!!picked[i]}
                         onChange={() => setPicked((p) => ({ ...p, [i]: !p[i] }))}
                         className="mt-1 accent-[var(--accent)]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white flex items-center gap-2">
                      {b.nombre}
                      {b.rating ? <span className="text-[var(--warn)] text-xs flex items-center gap-0.5"><Star className="w-3 h-3 fill-current" />{b.rating}</span> : null}
                    </div>
                    <div className="label-mono mt-0.5 truncate">{[b.telefono, b.web, b.direccion].filter(Boolean).join(" · ")}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}
        <p className="text-[10px] text-[var(--text-dim)] leading-relaxed">
          <MapPin className="w-3 h-3 inline mr-1" />Datos públicos de Google. Revisa y contacta con cabeza (personalizado, con opción de baja). No hagas spam.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-[var(--border)] bg-[var(--surface)] p-5 space-y-3">
          <div className="label-mono flex items-center gap-2"><UserPlus className="w-3.5 h-3.5" /> AÑADIR PROSPECTO</div>
          <Input placeholder="Nombre del negocio *" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="rounded-none bg-[var(--bg)] border-[var(--border)]" data-testid="cap-name" />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Contacto (tel/email)" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="rounded-none bg-[var(--bg)] border-[var(--border)]" />
            <Input placeholder="Sector" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} className="rounded-none bg-[var(--bg)] border-[var(--border)]" />
          </div>
          <Input placeholder="Notas (opcional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none bg-[var(--bg)] border-[var(--border)]" />
          <Button onClick={add} className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="cap-add">Añadir</Button>
        </div>
        <div className="border border-[var(--border)] bg-[var(--surface)] p-5 space-y-3">
          <div className="label-mono flex items-center gap-2"><Upload className="w-3.5 h-3.5" /> IMPORTAR EN LOTE</div>
          <Textarea rows={4} value={bulk} onChange={(e) => setBulk(e.target.value)}
                    placeholder={"Una línea por negocio:\nBar Pepe; 600111222; restaurante\nPelu Ana; ana@x.com; peluquería"}
                    className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono text-xs" />
          <Button onClick={importBulk} variant="outline" className="rounded-none">Importar</Button>
        </div>
      </div>

      {/* Selector de acción de envío */}
      {actions.length > 0 && (
        <div className="flex items-center gap-3">
          <Label className="label-mono">Enviar mediante:</Label>
          <select value={sendAction} onChange={(e) => setSendAction(e.target.value)}
                  className="rounded-none bg-[var(--bg)] border border-[var(--border)] px-3 py-1.5 text-sm text-white">
            {actions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      )}

      {/* Lista */}
      <div className="space-y-3">
        {prospects.length === 0 && <div className="text-sm text-[var(--text-dim)] font-mono">Sin prospectos aún.</div>}
        {prospects.map((p) => (
          <div key={p.id} className="border border-[var(--border)] bg-[var(--surface)] p-5" data-testid={`prospect-${p.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <div className="text-sm font-semibold text-white">{p.business_name}</div>
                <div className="label-mono mt-0.5">{[p.sector, p.contact].filter(Boolean).join(" · ")}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="label-mono text-[10px]" style={{ color: STATUS_COLOR[p.status] }}>{STATUS[p.status] || p.status}</span>
                <button onClick={() => del(p)} className="text-[var(--text-dim)] hover:text-[var(--danger)]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <Textarea rows={2} value={p.message || ""} onChange={(e) => saveMsg(p, e.target.value)}
                      placeholder="Aún sin mensaje — pulsa 'Redactar con IA'"
                      className="rounded-none bg-[var(--bg)] border-[var(--border)] text-sm" />
            <div className="flex items-center gap-2 mt-2">
              <Button onClick={() => draft(p)} disabled={busy === p.id + "-draft"} variant="outline" className="rounded-none h-8" data-testid={`cap-draft-${p.id}`}>
                <Sparkles className="w-3.5 h-3.5 mr-1" /> {busy === p.id + "-draft" ? "Redactando…" : "Redactar con IA"}
              </Button>
              <Button onClick={() => send(p)} disabled={busy === p.id + "-send" || !p.message} className="rounded-none h-8 bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid={`cap-send-${p.id}`}>
                <Send className="w-3.5 h-3.5 mr-1" /> {busy === p.id + "-send" ? "Enviando…" : "Enviar"}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
